import { fallbackLng } from 'i18n-settings';
import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import type { EditingLanguageStore } from './types';

// ------------- Constants -------------

const STORAGE_KEY = 'editingLanguage';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

// ------------- Cookie Storage Adapter -------------

const cookieStorage: StateStorage = {
  getItem: (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match === null ? null : decodeURIComponent(match[1] ?? '');
  },
  setItem: (name: string, value: string): void => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  },
  removeItem: (name: string): void => {
    document.cookie = `${name}=; path=/; max-age=0`;
  },
};

// ------------- Helpers -------------

/**
 * Picks the default editing language from the editable list.
 * Priority: preferredLanguage (UI locale) if editable → 'en' if editable → first editable.
 */
function pickDefaultLanguage(langs: string[], preferredLanguage?: string): string {
  if (preferredLanguage !== undefined && preferredLanguage !== '' && langs.includes(preferredLanguage)) {
    return preferredLanguage;
  }
  if (langs.includes(fallbackLng)) {
    return fallbackLng;
  }
  return langs[0] ?? fallbackLng;
}

// ------------- Store Factory -------------

export function createEditingLanguageStore() {
  return createStore<EditingLanguageStore>()(
    persist(
      (set, get) => ({
        // ------------- Initial State -------------
        // Empty = "undecided". A present cookie rehydrates this synchronously to the
        // remembered value; otherwise setEditableLanguages seeds the default once.
        editingLanguage: '',
        editableLanguages: [],
        hasUserPickedThisSession: false,
        isExplicitChoice: false,

        // ------------- Actions -------------

        setEditingLanguage: (lang: string): void => {
          const { editingLanguage: current } = get();
          // Mark as an explicit choice even when the value is unchanged, so a later
          // setEditableLanguages call can never re-default over the user.
          if (lang === current) {
            set({ hasUserPickedThisSession: true, isExplicitChoice: true });
            return;
          }
          set({ editingLanguage: lang, hasUserPickedThisSession: true, isExplicitChoice: true });
        },

        setEditableLanguages: (langs: string[], preferredLanguage?: string): void => {
          const { editingLanguage: current, hasUserPickedThisSession, isExplicitChoice } = get();

          set({ editableLanguages: langs });

          if (langs.length === 0) {
            return;
          }

          const currentIsEditable = current !== '' && langs.includes(current);

          // Keep a DELIBERATE pick as long as it is still editable. A programmatic seed
          // (isExplicitChoice === false) is deliberately NOT kept here, so it re-derives
          // from the current UI locale each session — otherwise a fallback 'en' seeded in
          // an earlier English-UI (or cache-cleared) load would stick and permanently
          // block the UI-locale default.
          if (currentIsEditable === true && isExplicitChoice === true) {
            return;
          }

          // A pick made THIS session that is no longer editable: re-pick a safe value
          // ('en' → first editable) rather than jumping to the UI locale.
          if (hasUserPickedThisSession === true) {
            set({ editingLanguage: langs.includes(fallbackLng) ? fallbackLng : (langs[0] ?? fallbackLng) });
            return;
          }

          // Seed the default from the UI locale. Covers: no cookie (undecided), a stale
          // seed persisted by a previous session, or a rehydrated pick that is no longer
          // editable. UI locale (if editable) → 'en' (if editable) → first editable.
          set({ editingLanguage: pickDefaultLanguage(langs, preferredLanguage) });
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => cookieStorage),
        version: 2,
        // Migrate legacy (v0/v1) cookies. Before this fix the store persisted the
        // editingLanguage on every set() — including a programmatic seed — with no
        // marker for whether it was a deliberate pick. A fallback 'en' therefore looked
        // identical to a chosen 'en' and stuck, blocking the UI-locale default (the
        // reported regression). Reconstruct isExplicitChoice for old cookies: a non-'en'
        // value was almost certainly a deliberate pick (seeds default to the UI locale
        // or 'en'), so keep it and mark it explicit; treat 'en' (and '') as a
        // non-explicit seed so it re-seeds from the UI locale on the next sync.
        migrate: (persistedState, version) => {
          // zustand types persistedState as `unknown`; we only ever persist
          // `{ editingLanguage, isExplicitChoice }`, so this narrow cast is safe.
          const persisted = persistedState as { editingLanguage?: string; isExplicitChoice?: boolean } | undefined;
          const editingLanguage = persisted?.editingLanguage ?? '';
          if (version < 2) {
            const wasDeliberatePick = editingLanguage !== '' && editingLanguage !== fallbackLng;
            return wasDeliberatePick
              ? { editingLanguage, isExplicitChoice: true }
              : { editingLanguage: '', isExplicitChoice: false };
          }
          return { editingLanguage, isExplicitChoice: persisted?.isExplicitChoice ?? false };
        },
        // Persist a value ONLY when it is a deliberate pick; a programmatic seed persists
        // as '' so a returning session re-derives it from the current UI locale instead
        // of resurrecting a stale fallback. hasUserPickedThisSession is in-memory only.
        partialize: state => ({
          editingLanguage: state.isExplicitChoice === true ? state.editingLanguage : '',
          isExplicitChoice: state.isExplicitChoice,
        }),
      },
    ),
  );
}

// ------------- Store Type -------------

export type EditingLanguageStoreApi = ReturnType<typeof createEditingLanguageStore>;
